import { useTheme } from "@/app/providers/theme-provider"
import { KLERI_ASPECT, KLERI_PATH, KLERI_VIEWBOX } from "@/components/ui/kleri-logo-utils"
import { useBothLogosValidation } from "@/hooks/use-logo-validation"
import { getInjectedAsset, injectedAspectRatio } from "@/lib/runtime-branding"
import { cn } from "@/lib/utils"

// Create URLs for external logo files using Vite's import.meta.url
// These URLs are resolved relative to this module and processed by Vite at build time
// We check at runtime if the files actually exist before using them
const logoLightUrl = new URL("./large-logo-light.svg", import.meta.url).href
const logoDarkUrl = new URL("./large-logo-dark.svg", import.meta.url).href

type LargeLogoProps = {
  className?: string
  onClick?: () => void
}

/**
 * Embedded SVG fallback - used when external logo files don't exist
 * This is the default Kleri wordmark (main branch)
 * Customer branches should replace this with their own logo
 */
function EmbeddedLargeLogoSVG({ className, onClick }: LargeLogoProps) {
  return (
    <svg
      className={cn("", className)}
      fill="currentColor"
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      viewBox={KLERI_VIEWBOX}
      xmlns="http://www.w3.org/2000/svg"
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault()
                onClick()
              }
            }
          : undefined
      }
    >
      <path d={KLERI_PATH} />
    </svg>
  )
}

export function LargeLogo({ className, onClick }: LargeLogoProps) {
  const { theme } = useTheme()
  const logoStatus = useBothLogosValidation(logoLightUrl, logoDarkUrl)

  // Use external logos with theme switching
  let effectiveTheme: "light" | "dark"
  if (theme === "system") {
    effectiveTheme = window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"
  } else {
    effectiveTheme = theme
  }

  // An admin-uploaded logo wins over the build-time file (#2741), and skips the
  // validation fetch: the server only injects a ref for an asset it stored, so
  // there is nothing to probe and no "pending" placeholder frame.
  const injectedLight = getInjectedAsset("logo_large_light")
  const injectedDark = getInjectedAsset("logo_large_dark")
  const injected =
    effectiveTheme === "dark" ? (injectedDark ?? injectedLight) : (injectedLight ?? injectedDark)

  if (!injected && logoStatus === "pending") {
    return <div aria-hidden className={cn(className)} style={{ aspectRatio: KLERI_ASPECT }} />
  }

  if (!injected && logoStatus === "invalid") {
    return <EmbeddedLargeLogoSVG className={className} onClick={onClick} />
  }

  const logoSrc = injected?.url ?? (effectiveTheme === "dark" ? logoDarkUrl : logoLightUrl)

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <img
      alt="Logo"
      className={cn("h-auto transition-opacity duration-200", className)}
      role={onClick ? "button" : undefined}
      src={logoSrc}
      style={{ aspectRatio: injectedAspectRatio(injected), objectFit: "contain" }}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault()
                onClick()
              }
            }
          : undefined
      }
    />
  )
}
